import { useEffect, useState } from 'react';
import './PerformanceMode.css';
import './ResponsiveLayout.css';

const SESSION_KEY = 'solyminx-low-performance';

function hasStoredFallback() {
  try {
    return window.sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function mediaMatches(query) {
  return typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
}

export function hasImmediateLowPerformanceHint() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  const params = new URLSearchParams(window.location.search);
  const forcedLowMode = params.get('performance') === 'low';
  const reducedMotion = mediaMatches('(prefers-reduced-motion: reduce)');
  const compactViewport = mediaMatches('(max-width: 900px)');
  const compactLaptopViewport = mediaMatches('(min-width: 901px) and (max-width: 1440px) and (max-height: 900px)');
  const mobileDevice = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  const memory = Number(navigator.deviceMemory || 0);
  const cores = Number(navigator.hardwareConcurrency || 0);

  // Start in the lighter mode before mounting the video on clearly constrained
  // hardware. This includes common 8 GB, 1366x768 laptop configurations.
  const constrainedMemory = memory > 0 && memory <= 4;
  const constrainedProcessor = cores > 0 && cores <= 2;
  const constrainedMobileProcessor = (compactViewport || mobileDevice) && cores > 0 && cores <= 4;
  const constrainedLaptop = compactLaptopViewport && memory > 0 && memory <= 8;
  const unknownMemoryLaptop = compactLaptopViewport && memory === 0 && cores > 0 && cores <= 4;

  return forcedLowMode
    || reducedMotion
    || constrainedMemory
    || constrainedProcessor
    || constrainedMobileProcessor
    || constrainedLaptop
    || unknownMemoryLaptop;
}

export default function useAdaptivePerformance() {
  const [isLowPerformance, setIsLowPerformance] = useState(
    () => hasStoredFallback() || hasImmediateLowPerformanceHint()
  );

  useEffect(() => {
    const enableFallback = () => {
      setIsLowPerformance(true);
      try {
        window.sessionStorage.setItem(SESSION_KEY, '1');
      } catch {
        // The fallback still works when storage is unavailable.
      }
    };

    if (hasImmediateLowPerformanceHint() || hasStoredFallback()) {
      enableFallback();
      return undefined;
    }

    let animationFrameId;
    let previousTime;
    let windowStartedAt;
    let measuredFrames = 0;
    let slowFrames = 0;
    let verySlowFrames = 0;
    let consecutiveSlowWindows = 0;
    let cancelled = false;

    const resetSample = (timestamp) => {
      previousTime = timestamp;
      windowStartedAt = timestamp;
      measuredFrames = 0;
      slowFrames = 0;
      verySlowFrames = 0;
    };

    const measureFrame = (timestamp) => {
      if (cancelled || isLowPerformance) return;

      if (document.visibilityState !== 'visible') {
        resetSample(timestamp);
        animationFrameId = requestAnimationFrame(measureFrame);
        return;
      }

      if (previousTime == null || windowStartedAt == null) {
        resetSample(timestamp);
        animationFrameId = requestAnimationFrame(measureFrame);
        return;
      }

      const frameTime = timestamp - previousTime;
      const elapsed = timestamp - windowStartedAt;
      previousTime = timestamp;
      measuredFrames += 1;
      if (frameTime > 34) slowFrames += 1;
      if (frameTime > 70) verySlowFrames += 1;

      // Severe jank should stop the video quickly instead of waiting for two
      // complete samples.
      if (elapsed >= 1200 && verySlowFrames >= 4) {
        enableFallback();
        return;
      }

      // Measure phones and PCs alike. Two consecutive windows avoid switching
      // modes because of one loading hitch while still reacting within seconds.
      if (elapsed >= 1800) {
        const fps = measuredFrames / (elapsed / 1000);
        const slowRatio = measuredFrames ? slowFrames / measuredFrames : 0;
        const windowIsSlow = fps < 50 || slowRatio > 0.18 || verySlowFrames >= 2;
        consecutiveSlowWindows = windowIsSlow ? consecutiveSlowWindows + 1 : 0;

        if (consecutiveSlowWindows >= 2) {
          enableFallback();
          return;
        }
        resetSample(timestamp);
      }

      animationFrameId = requestAnimationFrame(measureFrame);
    };

    animationFrameId = requestAnimationFrame(measureFrame);
    return () => {
      cancelled = true;
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [isLowPerformance]);

  return isLowPerformance;
}
